import { useState } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useTheme } from '@context/ThemeContext'
import { useDeviceType } from '@hooks/useDeviceType'
import { shareService } from '@services/ShareService'

/**
 * Hook for handling share functionality in end game modals
 *
 * Encapsulates the common share flow:
 * - Mobile-only validation
 * - Game-specific validation (via getShareBlockReason callback)
 * - Loading state management
 * - Error handling with i18n
 * - ShareService invocation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.getShareBlockReason - Returns null if sharing allowed, or i18n key string if blocked
 * @param {Function} options.buildShareData - Returns the data object to share
 * @param {string} [options.logPrefix='[useShareHandler]'] - Prefix for console logs
 * @returns {Object} Share handler state and actions
 *
 * @example
 * // In MemoramaEndModal
 * const { handleShare, isSharing, shareError, shareVisible } = useShareHandler({
 *   getShareBlockReason: () =>
 *     players.length !== 1 ? 'shared.share.singlePlayerOnly' : null,
 *   buildShareData: () => ({
 *     type: 'memorama',
 *     playerData: players[0],
 *     gameData: { boardSize, cardCount }
 *   })
 * })
 *
 * @example
 * // In PuzzleEndModal
 * const { handleShare, isSharing, shareError, shareVisible } = useShareHandler({
 *   getShareBlockReason: () =>
 *     !gameSnapshot ? 'shared.share.noSnapshot' : null,
 *   buildShareData: () => ({
 *     type: 'puzzle',
 *     difficulty,
 *     imageSrc: gameSnapshot.imageSrc
 *   })
 * })
 */
export function useShareHandler ({
  getShareBlockReason,
  buildShareData,
  logPrefix = '[useShareHandler]'
}) {
  const { t, tFunc } = useLanguage()
  const { theme } = useTheme()
  const { isMobile } = useDeviceType()
  const [isSharing, setIsSharing] = useState(false)
  const [shareError, setShareError] = useState('')

  // Calculate once per render for shareVisible
  const blockReason = getShareBlockReason()
  const shareVisible = isMobile && blockReason === null

  const handleShare = async () => {
    // Guard against double-click
    if (isSharing) return

    // Recalculate fresh to avoid stale closure
    const currentBlockReason = getShareBlockReason()

    // Mobile-only validation
    if (!isMobile) {
      setShareError(tFunc('shared.share.mobileOnly'))
      return
    }

    // Game-specific validation
    if (currentBlockReason) {
      setShareError(tFunc(currentBlockReason))
      return
    }

    setIsSharing(true)
    setShareError('')

    try {
      const shareData = buildShareData()
      const success = await shareService.share(shareData, t, theme)

      if (!success) {
        setShareError(tFunc('shared.share.shareError'))
      }
    } catch (error) {
      console.error(`${logPrefix} Share error:`, error)
      setShareError(tFunc('shared.share.shareError'))
    } finally {
      setIsSharing(false)
    }
  }

  return {
    handleShare,
    isSharing,
    shareError,
    shareVisible
  }
}
